import { useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import useFetch from "../Networks/useFetch";
import { Endpoint } from "./endpoint";

/* ─────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────── */

const SUB_THEMES = [
  "Optimization Models for Sustainable Resource Allocation",
  "Digital Transformation and Data-Driven Decision Making in OR",
  "Supply Chain Resilience and Sustainability in Developing Economies",
  "Smart Cities and Urban Systems Optimization",
  "Healthcare Systems Optimization and Public Health Planning",
  "Energy Systems Modeling and Renewable Integration",
  "Agricultural Systems Optimization and Food Security",
  "Data-Driven Governance and Algorithmic Fairness in Public Policy",
  "Circular Economy Optimization and Green Logistics",
];

const SPEAKERS = [
  {
    name: "Prof. Ibrahim Umar",
    role: "Vice Chancellor, Modibbo Adama University, Yola",
    tag: "Chief Host",
  },
  {
    name: "Dr. Umar M. Modibbo",
    role: "Dept. of Operations Research, MAU Yola — ORIDSAN President",
    tag: "Host",
  },
  {
    name: "Prof. Hector Cancela",
    role: "Universidad de la Republica, Uruguay — IFORS President",
    tag: null,
  },
  {
    name: "Prof. Youssef Masmoudi",
    role: "University of Sfax, Tunisia — AFROS President",
    tag: null,
  },
  {
    name: "Prof. Maria Lapina",
    role: "Dept. of Computational Mathematics & Cybernetics, North-Caucasus Federal University, Russia",
    tag: null,
  },
  {
    name: "Mr. Shoyinka Shodunke",
    role: "Executive, MTN Group IT Core Design and Delivery",
    tag: null,
  },
  {
    name: "Alhaji Sadiq Umar Daware",
    role: "Dan Lawan Adamawa — Chairman, Dabareji Farms",
    tag: null,
  },
  {
    name: "Prof. Benjamin Oyediran Oyelami",
    role: "Director General, National Mathematical Centre, Abuja",
    tag: null,
  },
  {
    name: "Prof. Ikeme John Dike",
    role: "Dept. of Operations Research, Modibbo Adama University, Yola",
    tag: null,
  },
];

const FEES = [
  { category: "Students", earlyBird: "₦10,000", late: "₦15,000" },
  { category: "Members", earlyBird: "₦20,000", late: "₦30,000" },
  { category: "Non-Members", earlyBird: "₦30,000", late: "₦40,000" },
  { category: "International", earlyBird: "$80", late: "$100" },
];

const IMPORTANT_DATES = [
  { item: "Abstracts Submission", date: null },
  { item: "Notification of Abstracts Acceptance", date: null },
  {
    item: "Submission of Slides (max 15) or Posters (A0 Portrait)",
    date: null,
  },
  { item: "Arrival of Conference Participants", date: "Tue. 1st Sept 2026" },
  { item: "Conference Opens", date: "Wed. 2nd Sept 2026" },
  { item: "Conference Closes", date: "Fri. 4th Sept 2026" },
];

const ABSTRACT_GUIDELINES = [
  { label: "Word limit", value: "Not more than 300 words" },
  { label: "Keywords", value: "At least 4 per abstract" },
  { label: "Submission", value: "Online — conference@oridsan.org.ng" },
  { label: "Font", value: "Times New Romans, 12pt" },
  { label: "Line spacing", value: "1.5" },
];

const PAYMENT = {
  accountName:
    "Operations Research Institute for Decision Sciences & Analytics of Nigeria",
  bank: "Fidelity Bank",
  naira: "5601058569",
  usd: "5250521027",
};

const CONTACTS = [
  {
    name: "Dr. Umar M. Modibbo",
    role: "ORIDSAN President",
    phone: "07064304585",
  },
  { name: "Mohammed Mijiyawa", role: "LOC Chairman", phone: "07031386699" },
];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const AVATAR_COLORS = [
  ["#0a4a35", "#c4e8dd"],
  ["#185fa5", "#b5d4f4"],
  ["#534ab7", "#cecbf6"],
  ["#993c1d", "#f5c4b3"],
  ["#3b6d11", "#c0dd97"],
  ["#993556", "#f4c0d1"],
  ["#5f5e5a", "#d3d1c7"],
  ["#ba7517", "#fac775"],
];

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "speakers", label: "Speakers" },
  { key: "themes", label: "Sub-Themes" },
  { key: "fees", label: "Fees & Dates" },
  { key: "info", label: "Submit & Pay" },
];

function initials(name) {
  return name
    .replace(/^(Prof\.|Dr\.|Mr\.|Alhaji)\s*/i, "")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

/* ─────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────── */
const Conference = ({ title = "" }) => {
  const { data: events, isLoading } = useFetch(`${Endpoint}/events`);
  const [tab, setTab] = useState("overview");

  return (
    <div style={s.page}>
      {/* ══ HERO BANNER — 2nd Conference ══ */}
      <div style={s.banner}>
        <div style={s.bannerInner}>
          <div style={s.bannerLeft}>
            <div style={s.bannerTopRow}>
              <span style={s.confEditionBadge}>
                2ND INTERNATIONAL HYBRID CONFERENCE
              </span>
              <span style={s.liveTag}>● Registrations Open</span>
            </div>
            <h1 style={s.bannerH1}>
              Operations Research for
              <br />
              <span style={s.bannerH1Accent}>Sustainable Development</span>
              <br />
              in Emerging Economies
            </h1>
            <p style={s.bannerTagline}>
              Navigating Complexity in the Digital Age
            </p>
            <div style={s.bannerMeta}>
              <span style={s.metaPill}>
                📅 Wed 2nd – Fri 4th September 2026
              </span>
              <span style={s.metaPill}>
                📍 Modibbo Adama University Yola, Nigeria
              </span>
              <span style={{ ...s.metaPill, ...s.metaPillGreen }}>Hybrid</span>
            </div>
            <p style={s.bannerOrg}>
              Organised by <strong>ORIDSAN</strong> in collaboration with
              Modibbo Adama University, Yola &nbsp;·&nbsp; Endorsed by{" "}
              <strong>IFORS</strong> &amp; <strong>AFROS</strong>
            </p>
          </div>
          <div style={s.bannerRight}>
            <div style={s.countdownBox}>
              <p style={s.countdownLabel}>Early Bird Deadline</p>
              <p style={s.countdownDate}>24 July 2026</p>
              <div style={s.divider} />
              <p style={s.countdownLabel}>Late Registration</p>
              <p style={s.countdownDate}>25 Aug 2026</p>
              <div style={s.divider} />
              <p style={s.countdownLabel}>Conference</p>
              <p style={s.countdownDate}>2 – 4 Sept 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* ══ TABS ══ */}
      <div style={s.tabBar}>
        {TABS.map((t) => (
          <button
            key={t.key}
            style={{ ...s.tabBtn, ...(tab === t.key ? s.tabActive : {}) }}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ BODY ══ */}
      <div style={s.body}>
        {/* ── OVERVIEW ── */}
        {tab === "overview" && (
          <div>
            {title && <h3 style={s.sectionTitle}>{title}</h3>}

            {isLoading && (
              <div style={s.loading}>
                <LoadingOutlined style={{ fontSize: 28, color: "#0a4a35" }} />
                <span
                  style={{ marginLeft: 10, fontSize: 14, color: "#6b7280" }}
                >
                  Loading events…
                </span>
              </div>
            )}

            {events &&
              [...events].reverse().map((ev) => (
                <div key={ev._id} style={s.eventCard}>
                  <div style={s.dateBox}>
                    <span style={s.dateDay}>{ev.date.slice(8, 10)}</span>
                    <span style={s.dateMon}>
                      {MONTHS[parseInt(ev.date.slice(5, 7), 10) - 1]}
                    </span>
                    <span style={s.dateYr}>{ev.date.slice(0, 4)}</span>
                  </div>
                  <div style={s.eventInfo}>
                    <p style={s.eventTitle}>{ev.title}</p>
                    <p style={s.eventMeta}>
                      📍 {ev.venue}
                      {ev.time ? `  ·  🕐 ${ev.time}` : ""}
                    </p>
                  </div>
                </div>
              ))}

            <p style={s.label}>At a Glance</p>
            <div style={s.glanceGrid}>
              {[
                { label: "Conference Dates", value: "2 – 4 Sept 2026" },
                { label: "Arrival Date", value: "1st Sept 2026" },
                { label: "Venue", value: "Yola, Nigeria" },
                { label: "Format", value: "Hybrid" },
                { label: "Abstract Limit", value: "300 words max" },
                { label: "Bursaries", value: "5 × ₦15,000" },
                { label: "Early Bird Closes", value: "24 July 2026" },
                { label: "Endorsed By", value: "IFORS & AFROS" },
              ].map((g, i) => (
                <div key={i} style={s.glanceCard}>
                  <span style={s.glanceLabel}>{g.label}</span>
                  <span style={s.glanceValue}>{g.value}</span>
                </div>
              ))}
            </div>

            {/* ── Previous Conference Highlight ── */}
            <div style={s.prevConferenceWrap}>
              <p style={{ ...s.label, marginTop: 32 }}>Previous Conference</p>
              <div style={s.prevHeroWrap}>
                <img
                  src="/conference-2025.jpeg"
                  alt="1st ORIDSAN International Conference 2025"
                  style={s.prevHeroImg}
                />
                <div style={s.prevHeroOverlay}>
                  <div style={s.prevHeroContent}>
                    <span style={s.prevEditionBadge}>
                      1st International Hybrid Conference · 2025
                    </span>
                    <p style={s.prevTitle}>
                      Operations Research and Globalization
                    </p>
                    <p style={s.prevSubtitle}>
                      In collaboration with University of Ilorin Business
                      School, Nigeria
                    </p>
                    <p style={s.prevBody}>
                      The inaugural ORIDSAN International Hybrid Conference was
                      held at the Ilorin Business School, University of Ilorin,
                      4–7 November 2025. Endorsed by IFORS &amp; AFROS, it
                      brought together researchers, practitioners and
                      policymakers from Nigeria and beyond to explore OR's role
                      in social, economic, technological and ecological
                      globalisation — establishing ORIDSAN as a leading platform
                      for OR scholarship in Africa.
                    </p>
                    <div style={s.prevMetaRow}>
                      <span style={s.prevMetaPill}>📅 4–7 Nov 2025</span>
                      <span style={s.prevMetaPill}>📍 Ilorin, Nigeria</span>
                      <span style={s.prevMetaPill}>🤝 IFORS &amp; AFROS</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── SPEAKERS ── */}
        {tab === "speakers" && (
          <div>
            <p style={s.label}>Distinguished Speakers &amp; Guests</p>
            <div style={s.speakerGrid}>
              {SPEAKERS.map((sp, i) => {
                const [bg, fg] = AVATAR_COLORS[i % AVATAR_COLORS.length];
                return (
                  <div
                    key={i}
                    style={{
                      ...s.speakerCard,
                      ...(sp.tag ? s.speakerFeatured : {}),
                    }}
                  >
                    <div style={{ ...s.avatar, background: bg, color: fg }}>
                      {initials(sp.name)}
                    </div>
                    <div style={s.speakerText}>
                      <p style={s.speakerName}>{sp.name}</p>
                      <p style={s.speakerRole}>{sp.role}</p>
                    </div>
                    {sp.tag && <span style={s.tagBadge}>{sp.tag}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── THEMES ── */}
        {tab === "themes" && (
          <div>
            <p style={s.label}>9 Conference Sub-Themes</p>
            <p style={s.themeMeta}>
              <strong>Conference Theme:</strong> Operations Research for
              Sustainable Development in Emerging Economies: Navigating
              Complexity in the Digital Age
            </p>
            <div style={s.themeGrid}>
              {SUB_THEMES.map((t, i) => (
                <div key={i} style={s.themeCard}>
                  <span style={s.themeNum}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span style={s.themeText}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── FEES & DATES ── */}
        {tab === "fees" && (
          <div>
            <p style={s.label}>Important Dates</p>
            <div style={s.dateList}>
              {IMPORTANT_DATES.map((d, i) => (
                <div key={i} style={s.dateRow}>
                  <span style={s.dateItem}>{d.item}</span>
                  {d.date ? (
                    <span style={s.datePill}>{d.date}</span>
                  ) : (
                    <span style={s.dateTba}>TBA</span>
                  )}
                </div>
              ))}
            </div>

            <p style={{ ...s.label, marginTop: 32 }}>Conference Fees</p>
            <div style={s.feeTableWrap}>
              <table style={s.feeTable}>
                <thead>
                  <tr>
                    <th style={s.th}>Category</th>
                    <th style={s.th}>
                      Early Bird
                      <br />
                      <span style={s.thSub}>1st May – 24th July</span>
                    </th>
                    <th style={s.th}>
                      Late Registration
                      <br />
                      <span style={s.thSub}>25th July – 25th August</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {FEES.map((row, i) => (
                    <tr key={i} style={i % 2 === 0 ? s.trEven : {}}>
                      <td style={s.td}>{row.category}</td>
                      <td style={{ ...s.td, ...s.tdGreen }}>{row.earlyBird}</td>
                      <td style={s.td}>{row.late}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={s.bursaryNote}>
              ✦ Five (5) bursaries of ₦15,000 available for eligible members to
              attend and present papers (eligibility applies).
            </p>
          </div>
        )}

        {/* ── SUBMIT & PAY ── */}
        {tab === "info" && (
          <div>
            <p style={s.label}>Abstract Submission Guidelines</p>
            <div style={s.guideCard}>
              {ABSTRACT_GUIDELINES.map((g, i) => (
                <div key={i} style={s.guideRow}>
                  <span style={s.guideKey}>{g.label}</span>
                  <span style={s.guideVal}>{g.value}</span>
                </div>
              ))}
            </div>

            <p style={{ ...s.label, marginTop: 32 }}>Payment Details</p>
            <div style={s.infoCard}>
              <p style={s.infoLine}>
                <strong>Account Name:</strong> {PAYMENT.accountName}
              </p>
              <p style={s.infoLine}>
                <strong>Bank:</strong> {PAYMENT.bank}
              </p>
              <p style={s.infoLine}>
                <strong>Naira Account No:</strong> {PAYMENT.naira}
              </p>
              <p style={s.infoLine}>
                <strong>USD Account No:</strong> {PAYMENT.usd}
              </p>
            </div>

            <p style={{ ...s.label, marginTop: 32 }}>Contact Persons</p>
            <div style={s.contactGrid}>
              {CONTACTS.map((c, i) => (
                <div key={i} style={s.contactCard}>
                  <p style={s.contactName}>{c.name}</p>
                  <p style={s.contactRole}>{c.role}</p>
                  <a href={`tel:${c.phone}`} style={s.contactPhone}>
                    {c.phone}
                  </a>
                </div>
              ))}
            </div>

            <div style={s.linksRow}>
              <a href="mailto:conference@oridsan.org.ng" style={s.linkBtn}>
                ✉ conference@oridsan.org.ng
              </a>
              <a href="mailto:oridsan2@gmail.com" style={s.linkBtn}>
                ✉ oridsan2@gmail.com
              </a>
              <a
                href="https://www.oridsan.org.ng"
                target="_blank"
                rel="noreferrer"
                style={s.linkBtn}
              >
                🌐 www.oridsan.org.ng
              </a>
              <a
                href="https://portal.oridsan.org.ng"
                target="_blank"
                rel="noreferrer"
                style={{ ...s.linkBtn, ...s.linkBtnGreen }}
              >
                Join as Member →
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const f = "'Segoe UI', system-ui, sans-serif";

const s = {
  page: {
    fontFamily: f,
    width: "100%",
    background: "#fff",
  },

  /* ── Banner ── */
  banner: {
    background:
      "linear-gradient(135deg, #062c20 0%, #0a4a35 55%, #0d5c42 100%)",
    padding: "60px 5% 52px",
  },
  bannerInner: {
    display: "flex",
    gap: 32,
    alignItems: "flex-start",
    flexWrap: "wrap",
    maxWidth: 1200,
    margin: "0 auto",
  },
  bannerLeft: { flex: 1, minWidth: 260 },
  bannerTopRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  confEditionBadge: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.1em",
    background: "rgba(255,255,255,0.15)",
    color: "#9dcfbf",
    padding: "4px 12px",
    borderRadius: 20,
  },
  liveTag: {
    fontSize: 11,
    fontWeight: 600,
    color: "#86efac",
    background: "rgba(34,197,94,0.15)",
    border: "1px solid rgba(34,197,94,0.3)",
    padding: "3px 10px",
    borderRadius: 20,
  },
  bannerH1: {
    margin: "0 0 8px",
    fontSize: 42,
    fontWeight: 800,
    lineHeight: 1.2,
    color: "#fff",
    letterSpacing: "-0.02em",
  },
  bannerH1Accent: { color: "#5dd4ac" },
  bannerTagline: {
    margin: "0 0 22px",
    fontSize: 16,
    color: "#6ab89e",
    fontStyle: "italic",
  },
  bannerMeta: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 },
  metaPill: {
    fontSize: 13,
    background: "rgba(255,255,255,0.1)",
    color: "#e0f4ee",
    padding: "6px 14px",
    borderRadius: 20,
  },
  metaPillGreen: {
    background: "rgba(34,197,94,0.15)",
    color: "#86efac",
    border: "1px solid rgba(34,197,94,0.3)",
  },
  bannerOrg: {
    margin: 0,
    fontSize: 13,
    color: "#6ab89e",
    lineHeight: 1.6,
  },
  bannerRight: { flexShrink: 0 },
  countdownBox: {
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 14,
    padding: "24px 28px",
    minWidth: 200,
  },
  countdownLabel: {
    margin: "0 0 2px",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#6ab89e",
  },
  countdownDate: {
    margin: "0 0 6px",
    fontSize: 20,
    fontWeight: 700,
    color: "#fff",
  },
  divider: {
    height: 1,
    background: "rgba(255,255,255,0.1)",
    margin: "10px 0",
  },

  /* ── Tabs ── */
  tabBar: {
    display: "flex",
    borderBottom: "1px solid #e5e7eb",
    background: "#fff",
    overflowX: "auto",
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: "0 5%",
  },
  tabBtn: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "16px 22px",
    fontSize: 14,
    fontWeight: 500,
    border: "none",
    borderBottom: "3px solid transparent",
    background: "transparent",
    color: "#6b7280",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "color 0.15s",
  },
  tabActive: {
    color: "#0a4a35",
    borderBottomColor: "#0a4a35",
    fontWeight: 600,
  },
  tabBadge: {
    fontSize: 9,
    fontWeight: 700,
    background: "#e5e7eb",
    color: "#6b7280",
    padding: "1px 6px",
    borderRadius: 10,
  },

  /* ── Body ── */
  body: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "48px 5% 72px",
  },
  label: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#0a4a35",
    marginBottom: 18,
    marginTop: 0,
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: "#0a4a35",
    marginBottom: 16,
    marginTop: 0,
  },
  loading: {
    display: "flex",
    alignItems: "center",
    padding: "20px 0",
  },

  /* ── Event cards ── */
  eventCard: {
    display: "flex",
    gap: 14,
    padding: "12px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    marginBottom: 10,
    background: "#f9fafb",
    alignItems: "flex-start",
  },
  dateBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    background: "#0a4a35",
    color: "#fff",
    borderRadius: 8,
    padding: "8px 12px",
    minWidth: 52,
    flexShrink: 0,
  },
  dateDay: { fontSize: 22, fontWeight: 700, lineHeight: 1 },
  dateMon: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginTop: 2,
  },
  dateYr: { fontSize: 9, color: "#9dcfbf", marginTop: 1 },
  eventInfo: { flex: 1 },
  eventTitle: {
    margin: "0 0 5px",
    fontWeight: 700,
    fontSize: 16,
    color: "#111827",
  },
  eventMeta: { margin: 0, fontSize: 14, color: "#6b7280" },

  /* ── Glance grid ── */
  glanceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 12,
    marginTop: 8,
  },
  glanceCard: {
    background: "#f0faf6",
    border: "1px solid #bbddd2",
    borderRadius: 12,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },
  glanceLabel: { fontSize: 13, color: "#6b7280" },
  glanceValue: { fontSize: 17, fontWeight: 700, color: "#0a4a35" },

  /* ── Speakers ── */
  speakerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 14,
  },
  speakerCard: {
    display: "flex",
    gap: 14,
    padding: "18px 20px",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fafafa",
    alignItems: "flex-start",
    position: "relative",
  },
  speakerFeatured: { border: "1.5px solid #0a4a35", background: "#f0faf6" },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 16,
    flexShrink: 0,
  },
  speakerText: { flex: 1 },
  speakerName: {
    margin: "0 0 4px",
    fontWeight: 700,
    fontSize: 15,
    color: "#111827",
  },
  speakerRole: { margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.5 },
  tagBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    background: "#0a4a35",
    color: "#fff",
    padding: "2px 8px",
    borderRadius: 20,
  },

  /* ── Themes ── */
  themeMeta: {
    fontSize: 15,
    color: "#6b7280",
    marginBottom: 20,
    marginTop: -8,
    lineHeight: 1.6,
  },
  themeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 12,
  },
  themeCard: {
    display: "flex",
    gap: 14,
    padding: "16px 18px",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fafafa",
    alignItems: "flex-start",
  },
  themeNum: {
    fontSize: 24,
    fontWeight: 800,
    color: "#bbddd2",
    flexShrink: 0,
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
  },
  themeText: {
    fontSize: 16,
    color: "#1f2937",
    lineHeight: 1.55,
    fontWeight: 500,
  },

  /* ── Dates ── */
  dateList: { display: "flex", flexDirection: "column", gap: 8 },
  dateRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    padding: "10px 14px",
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
  },
  dateItem: { fontSize: 16, color: "#374151" },
  datePill: {
    fontSize: 13,
    fontWeight: 700,
    background: "#0a4a35",
    color: "#fff",
    padding: "4px 12px",
    borderRadius: 20,
    whiteSpace: "nowrap",
  },
  dateTba: {
    fontSize: 13,
    fontWeight: 600,
    background: "#f3f4f6",
    color: "#9ca3af",
    padding: "4px 12px",
    borderRadius: 20,
  },

  /* ── Fee table ── */
  feeTableWrap: { overflowX: "auto" },
  feeTable: { width: "100%", borderCollapse: "collapse", fontSize: 15 },
  th: {
    background: "#0a4a35",
    color: "#fff",
    padding: "14px 20px",
    textAlign: "left",
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: "0.03em",
  },
  thSub: { fontWeight: 400, fontSize: 12, opacity: 0.75 },
  td: {
    padding: "12px 20px",
    borderBottom: "1px solid #e5e7eb",
    color: "#374151",
  },
  tdGreen: { fontWeight: 700, color: "#0a4a35" },
  trEven: { background: "#f9fafb" },
  bursaryNote: {
    marginTop: 16,
    fontSize: 14,
    color: "#6b7280",
    lineHeight: 1.6,
    background: "#fefce8",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "12px 16px",
  },

  /* ── Guidelines ── */
  guideCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    overflow: "hidden",
  },
  guideRow: {
    display: "flex",
    gap: 16,
    padding: "10px 16px",
    borderBottom: "1px solid #f3f4f6",
    flexWrap: "wrap",
  },
  guideKey: {
    fontSize: 14,
    color: "#6b7280",
    width: 160,
    flexShrink: 0,
    fontWeight: 500,
  },
  guideVal: { fontSize: 15, color: "#111827", flex: 1, fontWeight: 500 },

  /* ── Payment / Contact ── */
  infoCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "16px 20px",
    background: "#fafafa",
  },
  infoLine: {
    margin: "0 0 10px",
    fontSize: 15,
    color: "#374151",
    lineHeight: 1.6,
  },
  contactGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
    marginTop: 4,
  },
  contactCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "18px 20px",
    background: "#fafafa",
  },
  contactName: {
    margin: "0 0 3px",
    fontWeight: 700,
    fontSize: 15,
    color: "#111827",
  },
  contactRole: { margin: "0 0 10px", fontSize: 14, color: "#6b7280" },
  contactPhone: {
    fontSize: 14,
    color: "#0a4a35",
    fontWeight: 600,
    textDecoration: "none",
  },
  linksRow: { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 24 },
  linkBtn: {
    fontSize: 14,
    fontWeight: 600,
    padding: "10px 20px",
    border: "1px solid #d1d5db",
    borderRadius: 10,
    background: "#fff",
    color: "#374151",
    textDecoration: "none",
    display: "inline-block",
  },
  linkBtnGreen: {
    background: "#0a4a35",
    color: "#fff",
    border: "1px solid #0a4a35",
  },

  /* ── Previous Conference Hero ── */
  prevConferenceWrap: {},
  prevHeroWrap: {
    position: "relative",
    borderRadius: 16,
    overflow: "hidden",
    height: 460,
    marginTop: 4,
  },
  prevHeroImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  prevHeroOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to top, rgba(6,44,32,0.96) 0%, rgba(6,44,32,0.6) 50%, transparent 100%)",
    display: "flex",
    alignItems: "flex-end",
  },
  prevHeroContent: {
    padding: "40px 44px",
    maxWidth: 720,
  },
  prevEditionBadge: {
    display: "inline-block",
    marginBottom: 12,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    background: "rgba(255,255,255,0.18)",
    color: "#9dcfbf",
    padding: "4px 12px",
    borderRadius: 20,
  },
  prevTitle: {
    margin: "0 0 6px",
    fontSize: 28,
    fontWeight: 800,
    color: "#fff",
    letterSpacing: "-0.01em",
    lineHeight: 1.25,
  },
  prevSubtitle: {
    margin: "0 0 12px",
    fontSize: 15,
    color: "#9dcfbf",
    fontStyle: "italic",
  },
  prevBody: {
    margin: "0 0 18px",
    fontSize: 14,
    color: "rgba(255,255,255,0.82)",
    lineHeight: 1.75,
  },
  prevMetaRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  prevMetaPill: {
    fontSize: 12,
    fontWeight: 600,
    background: "rgba(255,255,255,0.15)",
    color: "#e0f4ee",
    padding: "5px 12px",
    borderRadius: 20,
  },
};

export default Conference;
